import { zonedTimeToUtc } from 'date-fns-tz'
import * as React from 'react'
import StringTranslation from '../interfaces/string-translation'
import { PaymentMethod, PaymentMethods } from './types/payment-method'
import ServiceType from './types/service-type'

type Action =
  | { type: 'SET_CART'; payload: Partial<StateProps> }
  | { type: 'RESET' }

type ProviderValue = {
  cart: StateProps
  setCart: (cart: Partial<StateProps>) => void
  isValidCart: () => boolean
  resetCart: () => void
}

export type Service = {
  serviceId: string
  serviceName: StringTranslation
  servicePriceExternalExcl: number
  serviceType?: ServiceType
  bookingPeriod?: {
    from: string
    to: string
  }
  outlet?: {
    capacities: {
      max?: number
    }[]
    capacityExceptions: {
      max?: number
    }[]
  }
  quantity: number
}

export enum OrderType {
  COUPON = 'COUPON', // redeemed coupons remain order type COUPON
  VOUCHER = 'VOUCHER',
  OFFER = 'OFFER', // Can be transformed into VOUCHER
}

export type CouponType = 'email' | 'print'

export type StateProps = {
  package?: {
    id: string
    name: StringTranslation
    price?: number
    services: Service[]
    isPublic?: boolean
    hasCapacities?: boolean
  }
  guidedTour?: {
    id?: string
    name?: StringTranslation
    priceExternalExcl?: number
  }
  mixer?: {
    [category: string]: {
      categoryId: string
      categoryName: StringTranslation
      categoryPriority: number
      categoryAgendaOrder: number
      services: Service[]
    }
  }

  coupon?: {
    buyAsCoupon?: boolean
    couponType?: CouponType
    couponId?: string
  }

  numberOfPackages?: number
  deliveryDate?: string
  deliveryTime?: string
  deliveryDateTime?: Date
  orderId?: string
  totalAmount?: number
  comment?: string
  paymentMethod: PaymentMethod
  paymentPageId?: string
}

const updateDateAndTime = (
  cart: StateProps,
  newCart: Partial<StateProps>
): Partial<StateProps> => {
  switch (true) {
    case newCart.deliveryTime == null && newCart.deliveryDate == null:
      return {}

    case newCart.deliveryDate === '' || newCart.deliveryTime === '':
      return { deliveryDateTime: undefined }

    case (cart.deliveryTime === newCart.deliveryTime ||
      (cart.deliveryTime != null && newCart.deliveryTime == null)) &&
      cart.deliveryDate !== newCart.deliveryDate:
      return { deliveryTime: undefined, deliveryDateTime: undefined }

    case (cart.deliveryDate != null || newCart.deliveryDate != null) &&
      (newCart.deliveryTime !== cart.deliveryTime ||
        cart.deliveryDate !== newCart.deliveryDate):
      return {
        deliveryDateTime: zonedTimeToUtc(
          `${newCart.deliveryDate || cart.deliveryDate}T${
            newCart.deliveryTime || cart.deliveryTime
          }`,
          'Europe/Zurich'
        ),
      }

    default:
      return {}
  }
}

const initialState: StateProps = {
  numberOfPackages: 2,
  paymentMethod: PaymentMethods.SAFERPAY,
}

const reducer = (cart: StateProps, action: Action): StateProps => {
  switch (action.type) {
    case 'RESET':
      return initialState
    case 'SET_CART': {
      const newCart = action.payload

      const dateAndTimeUpdate = updateDateAndTime(cart, newCart)
      return { ...cart, ...newCart, ...dateAndTimeUpdate }
    }
    default:
      return cart
  }
}
const store =
  typeof window !== `undefined` && window.localStorage.getItem('cart')
const localState = store && JSON.parse(store)

const CartContext = React.createContext({} as ProviderValue)

const CartProvider = ({ children }) => {
  const [cart, dispatch] = React.useReducer(reducer, localState || initialState)

  const setCart = (payload: Partial<StateProps>) =>
    dispatch({ type: 'SET_CART', payload })

  const isValidCart = () =>
    cart.package?.id !== '' &&
    cart.numberOfPackages !== undefined &&
    cart.deliveryDate !== '' &&
    cart.deliveryTime !== ''

  const resetCart = () => dispatch({ type: 'RESET' })

  React.useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart))
  }, [cart])

  return (
    <CartContext.Provider
      value={{
        cart,
        setCart,
        isValidCart,
        resetCart,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export default CartContext

// eslint-disable-next-line no-underscore-dangle
const _test_ = { updateCart: updateDateAndTime }
export { CartProvider, _test_ }
